import React, { useEffect } from 'react'
import { Formik } from 'formik'
import { Button, Tabs, Tab, Grid } from '@mui/material'
import {
  type AssetProjectUpdateDto,
  validateAssetProjectUpdateDto
} from '../../../api-schema-types/assets/AssetProjectCrudDtos'
import { useUpdateProjectMutation } from '../../../api-services/assets/projectApi'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'

import ErrorComponent from '../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../components/ui-widgets/LoadingComponent'
import { setNotification } from '../../components/ui-widgets/uiWidgetsSlice'
import EditDealModelBilling from './EditDealModelBilling'
import EditIdentifierStatus from './EditIdentifierStatus'
import EditWebsiteLocation from './EditWebsiteLocation'
import EditSolarProjectLifecycleDates from './EditSolarProjectLifecycleDates'
import EditSolarMetadata from './EditSolarMetadata'
import {
  selectOverrideLockedFields,
  selectTabIndex,
  setOverrideLockedFields,
  setTabIndex
} from './editSolarProjectSlice'
import EditCellOwnerAgreementsPanel from './EditCellOwnerAgreementsPanel'
import { formatDateStringAsApiFormat } from '../../../utility/formatters/DateFormatter'
import EditCrowdSalePanel from './EditCrowdSalePanel'

export interface EditSolarProjectFormProps {
  project: AssetProjectUpdateDto
  triggerRefetch: () => void
}

export interface EditSolarProjectComponentProps {
  project: AssetProjectUpdateDto
  overrideLockedFields: boolean
  triggerRefetch?: () => void
}

const EditSolarProjectForm: React.FC<EditSolarProjectFormProps> = ({ project, triggerRefetch }: EditSolarProjectFormProps) => {
  const dispatch = useAppDispatch()

  const overrideLockedFields = useAppSelector(selectOverrideLockedFields)
  const tabIndex = useAppSelector(selectTabIndex)

  // ================================================================================================================
  //  FORM SETUP
  // ================================================================================================================

  const updateProjectOnSubmit = async (values: AssetProjectUpdateDto): Promise<void> => {
    const updateProject = { ...values }

    const updateLifecycleDates = { ...values.lifecycleDates }
    updateLifecycleDates.campaignEnds = formatDateStringAsApiFormat(values.lifecycleDates.campaignEnds)
    updateLifecycleDates.crowdsaleStartDate = formatDateStringAsApiFormat(values.lifecycleDates.crowdsaleStartDate)
    updateLifecycleDates.crowdsaleEndDate = formatDateStringAsApiFormat(values.lifecycleDates.crowdsaleEndDate)
    updateLifecycleDates.cellOwnerAgreementEffectiveDate = formatDateStringAsApiFormat(values.lifecycleDates.cellOwnerAgreementEffectiveDate)
    updateLifecycleDates.earlyEnergyDate = formatDateStringAsApiFormat(values.lifecycleDates.earlyEnergyDate)
    updateLifecycleDates.supplierCommercialOperationDate = formatDateStringAsApiFormat(values.lifecycleDates.supplierCommercialOperationDate)
    updateLifecycleDates.assetLeaseCommercialOperationDate = formatDateStringAsApiFormat(values.lifecycleDates.assetLeaseCommercialOperationDate)

    // Map ZEROes (no selection) back to NULL. See projectsApiMapper.projectCrudMapper
    const updateOrganisations = { ...values.organisations }
    updateOrganisations.charityMemberId = values.organisations.charityMemberId === 0 ? null : values.organisations.charityMemberId
    updateOrganisations.solarConsumerId = values.organisations.solarConsumerId === 0 ? null : values.organisations.solarConsumerId
    updateOrganisations.solarInstallerId = values.organisations.solarInstallerId === 0 ? null : values.organisations.solarInstallerId

    const updateWebsite = { ...values.website }
    updateWebsite.marketingSector = values.website.marketingSector === '' ? null : values.website.marketingSector

    updateProject.organisations = updateOrganisations
    updateProject.lifecycleDates = updateLifecycleDates
    updateProject.overrideLockRequested = overrideLockedFields
    updateProject.website = updateWebsite

    await apiUpdateProjectTrigger({ ...updateProject })
    dispatch(setOverrideLockedFields(false))
  }

  const handleCancel = async (): Promise<void> => {
    dispatch(setNotification({
      open: true,
      severity: 'warning',
      message: 'Edit action cancelled. Project has been reloaded.'
    }))
    triggerRefetch()
  }

  // ================================================================================================================
  //  UI Handlers
  // ================================================================================================================

  const handleTabSelected = (event: React.ChangeEvent<unknown>, newValue: number): void => {
    dispatch(setTabIndex(newValue))
  }

  const toggleOverrideFields = (): void => {
    dispatch(setOverrideLockedFields(!overrideLockedFields))
  }

  // ================================================================================================================
  //  FORM MUTATION + RESULT
  // ================================================================================================================

  const [apiUpdateProjectTrigger, updateResult] = useUpdateProjectMutation()

  useEffect(() => {
    if (updateResult.isSuccess) {
      dispatch(setNotification({ open: true, severity: 'success', message: 'Project updated successfully' }))
    }

    if (updateResult.isError) {
      dispatch(setNotification({
        open: true,
        severity: 'error',
        message: RtkQueryHelper.mapResponseError(updateResult.error)
      }))
    }
  }, [updateResult.isSuccess, updateResult.isError])

  return (
    <>

      <Formik
        initialValues={project}
        validationSchema={validateAssetProjectUpdateDto}
        onSubmit={async (values, actions) => {
          await updateProjectOnSubmit(values)
        }}
      >
        {formikProps => (

          <form
            autoComplete={'off'}
            autoCorrect={'off'}
            onSubmit={formikProps.handleSubmit}
          >

            {!formikProps.isValid && (
              <Grid item md={12} sx={{ mt: 3 }}>
                <ErrorComponent title={'Form validation error'} message={JSON.stringify(formikProps.errors)}/>
              </Grid>
            )}

            {updateResult.isLoading && <LoadingComponent/>}

              <Tabs value={tabIndex} onChange={handleTabSelected} indicatorColor="primary" textColor="primary" centered>
              <Tab label="Identifiers & Status"/>
              <Tab label="Deal Model & Billing"/>
              <Tab label="Solar Project Metadata" />
              <Tab label="Website & Location"/>
              <Tab label="Dates"/>
              <Tab label="Cell Owner Agreements"/>
              <Tab label="Crowd Sale"/>
              {/* <Tab label="Graphing Data" /> */}
              {/* <Tab label="Forecast" /> */}
            </Tabs>

            {tabIndex === 0 && (
              <EditIdentifierStatus project={project} overrideLockedFields={overrideLockedFields} />
            )}

            {tabIndex === 1 && (
              <EditDealModelBilling project={project} overrideLockedFields={overrideLockedFields} />
            )}

            {tabIndex === 2 && (
              <EditSolarMetadata project={project} overrideLockedFields={overrideLockedFields} />
            )}

            {tabIndex === 3 && (
              <EditWebsiteLocation project={project} overrideLockedFields={overrideLockedFields} />
            )}

            {tabIndex === 4 && (
             <EditSolarProjectLifecycleDates project={project} overrideLockedFields={overrideLockedFields} />
            )}

            {tabIndex === 5 && (
              <EditCellOwnerAgreementsPanel project={project}
                                            overrideLockedFields={overrideLockedFields}
                                            triggerRefetch={triggerRefetch}/>
            )}

            {tabIndex === 6 && (
              <EditCrowdSalePanel project={project}
                                            overrideLockedFields={overrideLockedFields}
                                            triggerRefetch={triggerRefetch}/>
            )}

            <Grid item md={12} sx={{ mt: 3 }}>&nbsp;</Grid>

            <Grid container spacing={4}>
              <Grid item lg={9} sx={{ mt: 3 }}>
                <Button variant={'contained'} disabled={!formikProps.isValid} type={'submit'} sx={{ mr: 3 }} >
                  Update Project
                </Button>
                <Button type={'button'} variant={'text'} onClick={handleCancel}>
                  Cancel
                </Button>
              </Grid>
              <Grid item lg={3} sx={{ mt: 3, textAlign: 'right' }}>
                <Button type={'button'} variant={'contained'} color={'warning'}
                        disabled={!project.userPermissions.canOverrideLockedFields}
                        onClick={toggleOverrideFields}>
                  Toggle lock overrides
                </Button>
              </Grid>
            </Grid>

          </form>

        )}
      </Formik>

    </>
  )
}

export default EditSolarProjectForm
